/**
 * Первый заход: всплывающие окна, звук, уведомления и расширение — одним нажатием.
 *
 * Браузер не отдаёт разрешение на две вкладки из скрипта. Он отдаёт его после
 * того, как человек нажал «разрешить» у значка в адресной строке. Значок
 * появляется, только когда второе `window.open` из одного жеста уже закрыли.
 * Поэтому первый заход сам открывает две свои вкладки: без этого человек
 * узнаёт о запрете уже на живой вилке, когда вторая контора не открылась.
 *
 * Звук и уведомления спрашиваются тем же нажатием: жест один, и откладывать
 * их на отдельный экран — значит снова ловить отказ в момент ставки.
 *
 * Расширение сайт поставить не может: `chrome.webstore.install` больше нет.
 * Тем же жестом качаем архив (или открываем карточку магазина, если она есть)
 * и сразу спрашиваем мостик — стоит или молчит.
 */

import { pingExt } from '../confirm/ping';
import { notifyTest, soundTried, useAlarm } from './alarm';
import { openTab } from './act';

export const PERMIT_KEY = 'forq.next.permit';
const OFFERED_KEY = 'forq.ext.offered';
const PROXY_OFFERED_KEY = 'forq.proxy.offered';
export const PROXY_DOWNLOAD_URL = '/downloads/proxy/current/FORQ-Proxy-Setup.exe';

export function proxySupported(): boolean {
  return /Windows/i.test(navigator.userAgent) && !/ARM|aarch64/i.test(navigator.userAgent);
}

/** A download request is not installation confirmation. Never mark it installed. */
export function offerProxy(): boolean {
  if (!proxySupported()) return false;
  try {
    if (sessionStorage.getItem(PROXY_OFFERED_KEY) === '1') return false;
  } catch {}
  try {
    const a = document.createElement('a');
    a.href = PROXY_DOWNLOAD_URL;
    a.download = 'FORQ-Proxy-Setup.exe';
    a.rel = 'noopener';
    document.body.append(a);
    a.click();
    a.remove();
    try {
      sessionStorage.setItem(PROXY_OFFERED_KEY, '1');
    } catch {}
    return true;
  } catch {
    return false;
  }
}

/**
 * Расширение уже стоит, а вкладка открыта до вставки мостика — ping молчит.
 * Учить «поставь архив заново» нельзя: человек снимает карточку, слетает ключ.
 */
export const EXT_SILENT =
  'если значок FORQ уже в браузере — обновите эту вкладку. ставить заново нельзя: слетит ключ стола.';

/** Минимальная версия с актуальными исправлениями открытия и купона. */
export const EXT_MIN = '0.4.125';

export function extFresh(version: string | null): boolean {
  if (version === null || version === '') return false;
  const have = version.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const need = EXT_MIN.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const len = Math.max(have.length, need.length);
  for (let i = 0; i < len; i += 1) {
    const a = have[i] ?? 0;
    const b = need[i] ?? 0;
    if (a > b) return true;
    if (a < b) return false;
  }
  return true;
}

export function isDone(): boolean {
  try {
    const raw = localStorage.getItem(PERMIT_KEY);
    if (raw === null) return false;
    return (JSON.parse(raw) as { done?: boolean }).done === true;
  } catch {
    return false;
  }
}

export function markDone(): void {
  try {
    localStorage.setItem(PERMIT_KEY, JSON.stringify({ done: true }));
  } catch {
    // Приватный режим не помнит между визитами — на эту вкладку хватит состояния экрана.
  }
}

/** Своя страница, не контора: так Chrome показывает значок, а не чужой сайт. */
export function helperUrl(n: string): string {
  const base = import.meta.env.BASE_URL || '/';
  const root = base.endsWith('/') ? base : `${base}/`;
  return `${root}ok.html?n=${encodeURIComponent(n)}`;
}

/** Две свои вкладки. Пока сайту не разрешены всплывающие, вторая не откроется. */
export function probePopups(): boolean {
  const a = openTab(helperUrl('1'));
  const b = openTab(helperUrl('2'));
  return a !== null && b !== null;
}

/** Свой архив, не чужой CDN: тот же хост, что и лента. */
export function extZipUrl(): string {
  const base = import.meta.env.BASE_URL || '/';
  const root = base.endsWith('/') ? base : `${base}/`;
  return `${root}forq-extension.zip`;
}

/**
 * Карточка магазина, если уже есть. Пусто — тестеры живут на распакованном
 * архиве: модерация длится неделями, а ставить надо сегодня.
 */
export function storeUrl(): string | null {
  const raw = import.meta.env.VITE_EXT_STORE_URL;
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  return raw.trim();
}

/** Версия с метки, которую расширение ставит на документ при вставке. */
export function extSeen(): string | null {
  try {
    const raw = document.documentElement.getAttribute('data-forq-ext');
    return raw !== null && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Тем же жестом: архив или карточка магазина. После `await` браузер это
 * уже не разрешит — поэтому вызывается из `arm`, до любой паузы.
 */
export function offerExt(): void {
  if (extFresh(extSeen())) return;
  const store = storeUrl();
  if (store !== null) {
    openTab(store);
    return;
  }
  // Обновление — в уже стоящую папку. Zip качаем только при первой постановке:
  // старое расширение после скачивания снова просят «удали и поставь».
  if (extSeen() !== null) return;
  try {
    if (sessionStorage.getItem(OFFERED_KEY) === '1') return;
    sessionStorage.setItem(OFFERED_KEY, '1');
  } catch {
    // Приватный режим — качнем, если получится.
  }
  try {
    const a = document.createElement('a');
    a.href = extZipUrl();
    a.download = 'forq-extension.zip';
    a.rel = 'noopener';
    document.body.append(a);
    a.click();
    a.remove();
  } catch {
    // Скачивание — удобство. Жест уже потрачен на вкладки.
  }
}

/** Версия с метки или с рукопожатия. Метка есть раньше ping, если мостик уже вставлен. */
export async function hearExt(where: Window = window): Promise<string | null> {
  const seen = extSeen();
  if (seen !== null) return seen;
  const pong = await pingExt(where);
  return pong?.version ?? extSeen();
}

export interface Check {
  popups: boolean;
  sound: boolean;
  notify: boolean;
  ext: string | null;
}

export function missing(one: Check): string[] {
  const out: string[] = [];
  if (!one.popups) out.push('всплывающие окна');
  if (!one.sound) out.push('звук');
  if (!one.notify) out.push('уведомления');
  if (!extFresh(one.ext)) out.push('расширение');
  return out;
}

export function passed(one: Check): boolean {
  return missing(one).length === 0;
}

function notifyGranted(): boolean {
  try {
    return typeof Notification !== 'undefined' && Notification.permission === 'granted';
  } catch {
    return false;
  }
}

/** Что видно сразу после жеста, ещё до ответа расширения. */
export function glance(popups: boolean): Check {
  return {
    popups,
    sound: soundTried(),
    notify: notifyGranted(),
    ext: extSeen(),
  };
}

/**
 * Проверка после жеста: уведомление на открытой вкладке и рукопожатие.
 *
 * Звук уже прозвучал в `setSound`. Повторять тон здесь незачем — человек
 * услышал его или нет; мы можем сказать только, создался ли контекст.
 */
export async function inspect(popups: boolean, where: Window = window): Promise<Check> {
  // Разрешение уведомлений уже запрошено в `arm`. Ждать его здесь нельзя:
  // пока человек смотрит на системный вопрос, диалог обязан уже показывать
  // остальные строки и кнопку «повторить».
  const ext = await hearExt(where);
  return {
    popups,
    sound: soundTried(),
    notify: notifyGranted() && notifyTest(),
    ext,
  };
}

/**
 * Тем же жестом, пока браузер его не забыл: вкладки, звук, уведомления, архив.
 *
 * Порядок нельзя переставлять. `window.open` обязан идти до любого `await`
 * (разрешение уведомлений его содержит): вкладка после ожидания блокируется.
 * Скачивание — тоже: после паузы жест уже потрачен.
 */
export function arm(): boolean {
  const ok = probePopups();
  useAlarm.getState().setSound(true);
  void useAlarm.getState().setNotify(true);
  offerExt();
  offerProxy();
  return ok;
}
